import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, Link } from 'react-router-dom';
import { FiCheckCircle, FiAlertCircle, FiUpload, FiShoppingBag, FiArrowRight } from 'react-icons/fi';
import { supabase } from '../config/supabase';
import { useAuthContext } from '../hooks/useAuthContext';
import { useLanguage } from '../context/LanguageContext';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 640px) {
    padding: 24px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
  text-align: center;
  
  @media (max-width: 640px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  text-align: center;
  margin-bottom: 32px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  
  ${props => props.required && `
    &::after {
      content: ' *';
      color: #ef4444;
    }
  `}
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const UploadArea = styled.div`
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #f9fafb;
  
  &:hover {
    border-color: #5efba9;
    background: #f0fdf4;
  }
`;

const ImagePreview = styled.img`
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-top: 8px;
`;

const SubmitButton = styled.button`
  padding: 14px 24px;
  background: #5efba9;
  color: #1f2937;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;
  
  &:hover:not(:disabled) {
    background: #4de89e;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(94, 251, 169, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  background: #dcfce7;
  border: 1px solid #86efac;
  color: #166534;
  padding: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  border: 1px solid #fca5a5;
  color: #991b1b;
  padding: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const InfoBox = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
  padding: 16px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 24px;
`;

const ExistingStoreCard = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const StoreIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #5efba9 0%, #4ae398 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  
  svg {
    font-size: 36px;
    color: #1f2937;
  }
`;

const StoreName = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
`;

const StoreStatus = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 24px;
  
  ${props => props.active ? `
    background: #d1fae5;
    color: #065f46;
  ` : `
    background: #fef3c7;
    color: #92400e;
  `}
`;

const StoreMessage = styled.p`
  color: #6b7280;
  font-size: 16px;
  margin-bottom: 32px;
  line-height: 1.6;
`;

const AdminButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 32px;
  background: #5efba9;
  color: #1f2937;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;
  
  &:hover {
    background: #4de89e;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(94, 251, 169, 0.3);
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const generateSlug = (name) => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export default function SellWithUs() {
  const { isArabic, setLanguage, language } = useLanguage();

  const { session } = useAuthContext();

  // Force English language on this page - prevent any language changes
  useEffect(() => {
    if (language !== 'en') {
      setLanguage('en');
    }
  }, [language, setLanguage]);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    description: '',
    expected_income: '',
    img: '',
    insta: ''
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [existingBrand, setExistingBrand] = useState(null);
  const [checkingBrand, setCheckingBrand] = useState(true);
  const fileInputRef = React.createRef();

  // Check if user already has a brand
  useEffect(() => {
    const checkExistingBrand = async () => {
      if (!session?.user?.id) {
        setCheckingBrand(false);
        return;
      }

      try {
        // Check if user is a brand manager
        const { data: brandManager, error: managerError } = await supabase
          .from('brand_managers')
          .select('brand_id, brands(*)')
          .eq('profile_id', session.user.id)
          .single();

        if (brandManager?.brands) {
          setExistingBrand(brandManager.brands);
          setCheckingBrand(false);
          return;
        }

        // Also check if user created a brand
        const { data: createdBrand, error: createdError } = await supabase
          .from('brands')
          .select('*')
          .eq('created_by', session.user.id)
          .single();

        if (createdBrand) {
          setExistingBrand(createdBrand);
        }
      } catch (err) {
        // No existing brand found, which is fine
        console.log('No existing brand found for user');
      } finally {
        setCheckingBrand(false);
      }
    };

    checkExistingBrand();
  }, [session]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // For phone field, only allow numbers
    if (name === 'phone') {
      const numbersOnly = value.replace(/\D/g, ''); // Remove all non-digits
      setFormData(prev => ({
        ...prev,
        [name]: numbersOnly
      }));
      return;
    }
    
    // For brand name field, only allow English characters, numbers, spaces, and common punctuation
    if (name === 'name') {
      // Remove Arabic characters and other non-ASCII characters
      // Allow: English letters (a-z, A-Z), numbers (0-9), spaces, hyphens, apostrophes, and periods
      const englishOnly = value.replace(/[^\x00-\x7F]/g, '').replace(/[^a-zA-Z0-9\s\-'.]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: englishOnly
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    setError(null);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `brand_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('brands')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw new Error('Failed to upload image: ' + uploadError.message);
      }

      // For private buckets, use signed URL
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from('brands')
        .createSignedUrl(fileName, 31536000);

      if (signedUrlError) {
        // Fallback to public URL
        const { data: urlData } = supabase.storage
          .from('brands')
          .getPublicUrl(fileName);

        if (urlData?.publicUrl) {
          setFormData(prev => ({ ...prev, img: urlData.publicUrl.trim() }));
        } else {
          throw new Error('Failed to generate image URL');
        }
      } else {
        setFormData(prev => ({ ...prev, img: signedUrlData.signedUrl.trim() }));
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      if (!formData.name?.trim()) {
        throw new Error(
          isArabic ? 'اسم العلامة التجارية مطلوب.' : 'Brand name is required.'
        );
      }

      if (!formData.phone?.trim()) {
        throw new Error(
          isArabic ? 'رقم الهاتف مطلوب.' : 'Phone number is required.'
        );
      }

      if (!formData.description?.trim()) {
        throw new Error(
          isArabic ? 'وصف العلامة التجارية مطلوب.' : 'Brand description is required.'
        );
      }

      if (!formData.expected_income?.trim()) {
        throw new Error(
          isArabic ? 'المتوسط الشهري مطلوب.' : 'Monthly average is required.'
        );
      }

      if (!formData.insta?.trim()) {
        throw new Error(
          isArabic ? 'حساب الإنستغرام مطلوب.' : 'Instagram is required.'
        );
      }

      if (!formData.img || !formData.img.trim()) {
        throw new Error(
          isArabic
            ? 'صورة العلامة التجارية مطلوبة. الرجاء رفع صورة قبل التقديم.'
            : 'Brand image is required. Please upload an image before submitting.'
        );
      }

      const brandName = formData.name.trim();

      // Check if user already has a brand (prevent multiple brands per user)
      const { data: userBrands, error: userBrandsError } = await supabase
        .from('brands')
        .select('id, name, active')
        .eq('created_by', session?.user?.id);

      if (userBrandsError) {
        console.error('Error checking user brands:', userBrandsError);
      }

      if (userBrands && userBrands.length > 0) {
        throw new Error(isArabic
          ? `لديك بالفعل علامة تجارية مسجلة. لا يمكن إنشاء أكثر من علامة تجارية واحدة لكل حساب.`
          : `You already have a registered brand. Only one brand per account is allowed.`
        );
      }

      // Check if brand name already exists
      const { data: existingBrand, error: checkError } = await supabase
        .from('brands')
        .select('id, name, active')
        .ilike('name', brandName)
        .single();

      if (existingBrand) {
        throw new Error(isArabic
          ? `علامة تجارية بالاسم "${existingBrand.name}" موجودة مسبقاً. الرجاء اختيار اسم مختلف.`
          : `A brand with the name "${existingBrand.name}" already exists. Please choose a different name.`
        );
      }

      // Ensure user has a profile entry before creating brand
      if (session?.user?.id) {
        const { data: existingProfile, error: profileCheckError } = await supabase
          .from('profiles')
          .select('id')
          .eq('id', session.user.id)
          .maybeSingle();

        if (profileCheckError) {
          console.error('Error checking profile:', profileCheckError);
        }

        // If profile doesn't exist, create it
        if (!existingProfile) {
          const { error: profileCreateError } = await supabase
            .from('profiles')
            .upsert({
              id: session.user.id,
              full_name: session.user.email?.split('@')[0] || null,
            }, {
              onConflict: 'id'
            });

          if (profileCreateError) {
            console.error('Error creating profile:', profileCreateError);
            throw new Error(isArabic
              ? 'فشل في إنشاء ملف المستخدم. الرجاء المحاولة مرة أخرى.'
              : 'Failed to create user profile. Please try again.'
            );
          }
        }
      }

      // Create brand with active = false (pending approval)
      const brandData = {
        name: brandName,
        phone: formData.phone.trim() || null,
        description: formData.description.trim() || null,
        income: formData.expected_income.trim() || null,
        img: formData.img.trim() || null,
        insta: formData.insta.trim() || null,
        active: false, // Inactive by default, requires admin approval
        is_active: false, // Backward-compatible flag for older schema usage
        created_by: session?.user?.id || null,
      };

      const { data: insertedBrand, error: insertError } = await supabase
        .from('brands')
        .insert([brandData])
        .select()
        .single();

      if (insertError) {
        // Check if it's a duplicate name error
        if (insertError.message.includes('brands_name_key') || insertError.message.includes('duplicate key')) {
          throw new Error(isArabic
            ? `اسم العلامة التجارية "${brandName}" محجوز مسبقاً. الرجاء اختيار اسم مختلف.`
            : `The brand name "${brandName}" is already taken. Please choose a different name.`
          );
        }
        throw new Error(insertError.message);
      }

      // Generate website URL after slug is created
      if (insertedBrand.slug) {
        const website = `${window.location.origin}/${insertedBrand.slug}`;
        await supabase
          .from('brands')
          .update({ website: website })
          .eq('id', insertedBrand.id);
      }

      setSuccess(true);
      setFormData({ name: '', phone: '', description: '', expected_income: '', img: '', insta: '' });

      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);

    } catch (err) {
      console.error('Error submitting brand application:', err);
      setError(err.message || (isArabic
        ? 'فشل تقديم الطلب. الرجاء المحاولة مرة أخرى.'
        : 'Failed to submit application. Please try again.'
      ));
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading state while checking
  if (checkingBrand) {
    return (
      <Container>
        <Card>
          <LoadingState>
            {isArabic ? 'جاري التحقق...' : 'Checking...'}
          </LoadingState>
        </Card>
      </Container>
    );
  }

  // Show existing store UI
  if (existingBrand) {
    return (
      <Container>
        <Card>
          <ExistingStoreCard>
            <StoreIcon>
              <FiShoppingBag />
            </StoreIcon>
            <StoreName>{existingBrand.name}</StoreName>
            <StoreStatus active={existingBrand.active}>
              {existingBrand.active ? (
                <>
                  <FiCheckCircle />
                  {isArabic ? 'متجر نشط' : 'Active Store'}
                </>
              ) : (
                <>
                  <FiAlertCircle />
                  {isArabic ? 'في انتظار الموافقة' : 'Pending Approval'}
                </>
              )}
            </StoreStatus>
            <StoreMessage>
              {existingBrand.active ? (
                isArabic
                  ? 'لديك متجر نشط بالفعل! يمكنك إدارة منتجاتك وطلباتك من لوحة التحكم.'
                  : 'You already have an active store! You can manage your products and orders from the admin panel.'
              ) : (
                isArabic
                  ? 'طلب متجرك قيد المراجعة. سيتم إخطارك عند الموافقة عليه.'
                  : 'Your store application is under review. You\'ll be notified once it\'s approved.'
              )}
            </StoreMessage>
            {existingBrand.active && (
              <AdminButton to="/admin">
                {isArabic ? 'الذهاب إلى لوحة التحكم' : 'Go to Admin Panel'}
                <FiArrowRight />
              </AdminButton>
            )}
          </ExistingStoreCard>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Title>{isArabic ? 'بيع معنا' : 'Sell With Us'}</Title>
        <Subtitle>
          {isArabic
            ? 'تقدم لتصبح بائعاً على منصتنا'
            : 'Apply to become a seller on our platform'
          }
        </Subtitle>

        <InfoBox>
          <strong>{isArabic ? '📋 عملية التقديم:' : '📋 Application Process:'}</strong>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            <li>
              {isArabic
                ? 'املأ النموذج أدناه بمعلومات علامتك التجارية'
                : 'Fill out the form below with your brand details'
              }
            </li>
            <li>
              {isArabic
                ? 'سيتم مراجعة طلبك من قبل فريقنا'
                : 'Your application will be reviewed by our team'
              }
            </li>
            <li>
              {isArabic
                ? 'بعد الموافقة، سيتم تفعيل علامتك التجارية وتكون مرئية للعملاء'
                : 'Once approved, your brand will be activated and visible to customers'
              }
            </li>
            <li>
              {isArabic
                ? 'ستتلقى إشعاراً عندما يتم مراجعة طلبك'
                : 'You\'ll receive a notification when your application is reviewed'
              }
            </li>
          </ul>
        </InfoBox>

        {success && (
          <SuccessMessage>
            <FiCheckCircle size={20} />
            <div>
              <strong>
                {isArabic ? 'تم تقديم الطلب بنجاح!' : 'Application Submitted Successfully!'}
              </strong>
              <div style={{ fontSize: '14px', marginTop: '4px' }}>
                {isArabic
                  ? 'طلب علامتك التجارية قيد الموافقة. سيتم إعادة توجيهك إلى الصفحة الرئيسية قريباً.'
                  : 'Your brand application is pending approval. You\'ll be redirected to the home page shortly.'
                }
              </div>
            </div>
          </SuccessMessage>
        )}

        {error && (
          <ErrorMessage>
            <FiAlertCircle size={20} />
            <div>{error}</div>
          </ErrorMessage>
        )}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label required>Brand Name (English Only)</Label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="Enter your brand name (English only)"
              disabled={submitting || success}
              pattern="[a-zA-Z0-9\s\-'.]+"
              title="Brand name must be in English only (letters, numbers, spaces, hyphens, apostrophes, and periods)"
            />
            <small style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
              Only English letters, numbers, spaces, hyphens (-), apostrophes ('), and periods (.) are allowed.
            </small>
          </FormGroup>

          <FormGroup>
            <Label required>{isArabic ? 'رقم الهاتف' : 'Phone Number'}</Label>
            <Input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              maxLength={20}
              pattern="[0-9]{1,20}"
              inputMode="numeric"
              placeholder={isArabic ? 'مثال: 99123456' : 'e.g., 99123456'}
              disabled={submitting || success}
            />
            <small style={{ color: '#6b7280', fontSize: '12px' }}>
              {isArabic
                ? 'رقم الهاتف المحمول للتواصل والشحن (حتى 20 رقم)'
                : 'Mobile number for contact and shipping (up to 20 digits, numbers only)'
              }
            </small>
          </FormGroup>

          <FormGroup>
            <Label required>{isArabic ? 'وصف العلامة التجارية' : 'Brand Description'}</Label>
            <Textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={isArabic
                ? 'أخبرنا عن علامتك التجارية، منتجاتك، وما الذي يميزك...'
                : 'Tell us about your brand, products, and what makes you unique...'
              }
              required
              disabled={submitting || success}
            />
          </FormGroup>

          <FormGroup>
            <Label required>{isArabic ? 'المتوسط الشهري' : 'Monthly Average'}</Label>
            <Input
              type="text"
              name="expected_income"
              value={formData.expected_income}
              onChange={handleInputChange}
              placeholder={isArabic ? 'مثال: 500 - 1000 ريال عماني' : 'e.g., 500 - 1000 OMR'}
              required
              disabled={submitting || success}
            />
            <small style={{ color: '#6b7280', fontSize: '12px' }}>
              {isArabic
                ? 'أدخل المتوسط الشهري المتوقع من مبيعاتك'
                : 'Enter your expected monthly sales average'
              }
            </small>
          </FormGroup>

          <FormGroup>
            <Label required>Instagram</Label>
            <Input
              type="text"
              name="insta"
              value={formData.insta}
              onChange={handleInputChange}
              placeholder={isArabic
                ? '@اسم المستخدم أو instagram.com/اسم المستخدم'
                : '@username or instagram.com/username'
              }
              required
              disabled={submitting || success}
            />
            <small style={{ color: '#6b7280', fontSize: '12px' }}>
              {isArabic
                ? 'أدخل اسم مستخدم الإنستجرام (مثال: @username) أو الرابط الكامل'
                : 'Enter Instagram username (e.g., @username) or full URL'
              }
            </small>
          </FormGroup>

          <FormGroup>
            <Label required>{isArabic ? 'شعار/صورة العلامة التجارية' : 'Brand Logo/Image'}</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              disabled={uploadingImage || submitting || success}
              style={{ display: 'none' }}
            />
            <UploadArea onClick={() => !uploadingImage && !submitting && !success && fileInputRef.current?.click()}>
              {uploadingImage ? (
                <div>
                  <p style={{ color: '#5efba9', margin: 0 }}>
                    {isArabic ? 'جاري الرفع...' : 'Uploading...'}
                  </p>
                </div>
              ) : (
                <>
                  <FiUpload style={{ fontSize: '48px', color: '#9ca3af', marginBottom: '12px' }} />
                  <p style={{ color: '#6b7280', margin: 0 }}>
                    {isArabic ? 'انقر لرفع شعار/صورة العلامة التجارية' : 'Click to upload brand logo/image'}
                  </p>
                  <p style={{ fontSize: '12px', color: '#9ca3af', margin: '8px 0 0 0' }}>
                    {isArabic ? 'يدعم: JPG, PNG, GIF, WebP' : 'Supports: JPG, PNG, GIF, WebP'}
                  </p>
                </>
              )}
            </UploadArea>
            {!formData.img && (
              <small style={{ color: '#dc2626', fontSize: '12px' }}>
                {isArabic ? 'مطلوب رفع صورة العلامة التجارية' : 'Brand image upload is required'}
              </small>
            )}
            {formData.img && (
              <ImagePreview src={formData.img} alt={isArabic ? 'معاينة العلامة التجارية' : 'Brand preview'} />
            )}
          </FormGroup>

          <SubmitButton type="submit" disabled={submitting || success}>
            {submitting
              ? (isArabic ? 'جاري التقديم...' : 'Submitting...')
              : success
                ? (isArabic ? 'تم التقديم!' : 'Submitted!')
                : (isArabic ? 'تقديم الطلب' : 'Submit Application')
            }
          </SubmitButton>
        </Form>
      </Card>
    </Container>
  );
}